import "./polyfills";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import jwtDecode from "jwt-decode";
import { createBrowserHistory } from "history";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

import App from "./app/App";
import Actions from "./app/login/duck/actions";
import store, { persistor } from "./store";
import reportWebVitals from "./reportWebVitals";
import WebSocketProvider from "./webSocket/webSocket";
import NotificationsWebSocketContext from "./webSocket/notificationSocket";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import Operations from "./app/login/duck/operations";
import { createPosthogBeforeSend } from "./app/common/posthogBeforeSend";
import { activityBackendSync } from "./app/common/activity/activityBackendSync";
import activity from "./app/common/activity/activityLogger";
import { PersistGate } from "redux-persist/integration/react";
import { GOOGLE_OAUTH_CLIENT_ID, SENTRY_DSN } from "./app/common/constants";
import "react-tippy/dist/tippy.css";

// sentry for error tracking
import * as Sentry from "@sentry/react";
import SentryErrorPage from "./app/common/components/SentryErrorPage";
import ChunkLoadErrorFallback from "./app/common/components/ChunkLoadErrorFallback";
import { isChunkLoadError } from "./app/common/lazyWithRetry";
import UserPresence from "./app/presence/PresenceContainer";

// PostHog project API key + host hardcoded (US cloud). The project key is a
// public client-side token by design, so it is safe to ship in the bundle.
posthog.init("phc_A2gDBEQukbSFEQYYBnjRwGHqfJbSoqW2oKH9P4YsXbGU", {
  api_host: "https://us.i.posthog.com",
  defaults: "2026-01-30",
  // Resolved once, here, rather than per event: the origin cannot change
  // during a session. Drops everything from non-production origins, and
  // un-actionable exceptions (ResizeObserver notice, opaque "Script error.",
  // vendor-only stacks) everywhere.
  before_send: createPosthogBeforeSend(window.location.hostname),
});

/**  Redirect to dashboard if user already logged in */
let user = {};
try {
  user = JSON.parse(localStorage.getItem("user"));
} catch (e) {
  store.dispatch(Actions.logout());
  window.location.reload();
}

const authenticate = () => {
  let accessToken = null;
  let refreshToken = null;
  let user = null;
  try {
    accessToken = localStorage.getItem("accessToken");
    refreshToken = localStorage.getItem("refreshToken");
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    console.error("Local storage was corrupted.");
  }
  if (accessToken !== null && accessToken !== "" && user !== null) {
    try {
      if (jwtDecode(accessToken)) {
        store.dispatch(
          Operations.loginUserSuccess(accessToken, refreshToken, user)
        );
      }
    } catch (err) {
      console.error("Invalid token");
      store.dispatch(Actions.logout());
      window.location.reload();
    }
  } else {
    window.onload = function () {
      // Disable this inside i frames
      if (
        window.self === window.top &&
        localStorage.getItem("platformInvite") === null &&
        window.location.hostname !== "localhost" &&
        !["sop-builder"].some(function (part) {
          return window.location.pathname.includes(part);
        })
      ) {
        window.google?.accounts.id.initialize({
          client_id: `${GOOGLE_OAUTH_CLIENT_ID}`,
          callback: ({ credential }) => {
            store.dispatch(
              Operations.loginUser({
                providerName: "google",
                idToken: credential,
                inviteCode: new URL(window.location.href).searchParams.get(
                  "inviteCode"
                ),
                role: new URL(window.location.href).searchParams.get("role"),
              })
            );
          },
          auto_select: false,
          prompt_parent_id: "g_id_onload",
          cancel_on_tap_outside: true,
        });
        window.google?.accounts.id.prompt(); // also display the One Tap dialog
      }
    };
  }
};
authenticate();
window.onstorage = (e) => {
  if (e.key === "accessToken" && (e.oldValue === null || e.newValue === null)) {
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
};

const history = createBrowserHistory();

const SentryProvider = ({ children }) => {
  useEffect(() => {
    if (import.meta.env.PROD) {
      Sentry.init({
        dsn: SENTRY_DSN,
        replaysSessionSampleRate: 0.05,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.reactRouterV5BrowserTracingIntegration({
            history,
            tracePropagationTargets: [
              "nbyula.com",
              "nbl.one",
              "api.nbl.one",
              "api.nbyula.com",
              "argonaut.nbyula.com",
              "localhost",
              "127.0.0.1",
            ],
          }),
          Sentry.replayIntegration({
            maskAllText: false,
          }),
        ],
        tracesSampleRate: 1,
      });
    }
  }, []);

  if (!import.meta.env.PROD) {
    return children;
  }

  return (
    <Sentry.ErrorBoundary
      onError={(error, componentStack, eventId) => {
        // Sentry catches the crash, but the fallback replaces the app before
        // PostHog sees anything. Forward the exception so it reaches error
        // tracking and links to the session replay.
        posthog.captureException(error, {
          sentry_event_id: eventId,
          component_stack: componentStack,
        });
      }}
      fallback={({ error }) =>
        isChunkLoadError(error) ? (
          <ChunkLoadErrorFallback />
        ) : (
          <SentryErrorPage error={error} user={user} />
        )
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

createRoot(document.getElementById("root")).render(
  <PostHogProvider client={posthog}>
    <SentryProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <UserPresence>
            <NotificationsWebSocketContext>
              <WebSocketProvider>
                <App history={history} />
              </WebSocketProvider>
            </NotificationsWebSocketContext>
          </UserPresence>
        </PersistGate>
      </Provider>
    </SentryProvider>
  </PostHogProvider>
);

reportWebVitals(console.log);
activityBackendSync();
activity.initializeActivityLogger();
